import { useId, type KeyboardEvent, type ReactElement, type SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";

export interface LeaveDialogProps {
  onStay(): void;
  onGo(): void;
}

/** Zastaví událost na překryvu: okno výběru fotek pod ním by „klik mimo“ bralo jako zavření. */
const stop = (e: SyntheticEvent) => e.stopPropagation();

/**
 * Dialog odchodu během běžící dávky (F7.8): „Zůstat“ (výchozí, Esc) a „Odejít“. Leží nad oknem
 * výběru fotek (`z-50`), proto `z-[60]`; klik na překryv nic nedělá. Žádný `<form>`: okno
 * výběru leží nad formulářem editoru.
 */
export default function LeaveDialog({ onStay, onGo }: LeaveDialogProps): ReactElement {
  const { t } = useTranslation();
  const uid = useId();
  const titleId = `${uid}-title`;
  const textId = `${uid}-text`;

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onStay();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      onClick={stop}
      onMouseDown={stop}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={textId}
        data-leave-dialog=""
        onKeyDown={handleKeyDown}
        className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800"
      >
        <h3 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-white">
          {t("mediaUpload.leave.title")}
        </h3>
        <p id={textId} className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {t("mediaUpload.leave.text")}
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            data-leave-stay=""
            className="btn-primary"
            autoFocus
            onClick={onStay}
          >
            {t("mediaUpload.leave.stay")}
          </button>
          <button type="button" data-leave-go="" className="btn-ghost" onClick={onGo}>
            {t("mediaUpload.leave.go")}
          </button>
        </div>
      </div>
    </div>
  );
}
